import React from 'react';
import { 
  ArrowLeft, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw, 
  Upload
} from 'lucide-react';
import PRICING from '../config/pricing';

const JobHeader = ({ 
  job, 
  isProcessing,
  batchApplying = false
}) => {
  if (!job) return null;

  // Get provider info from job
  const provider = job?.provider || 'flickr';
  const providerName = provider.charAt(0).toUpperCase() + provider.slice(1);

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Your {providerName} Photos
            </h1>
            <p className="text-gray-600">
              Created: {new Date(job.createdAt).toLocaleString()}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Provider Badge */}
          <div className="flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
            <span className="mr-1">
              {provider === 'flickr' && '📷'}
              {provider === 'unsplash' && '🌄'}
              {provider === 'smugmug' && '📸'}
            </span>
            {providerName}
          </div>
          
          {job.status === 'completed' && (
            <div className="flex items-center text-sm text-green-600">
              <CheckCircle className="h-4 w-4 mr-2" />
              Completed
            </div>
          )}
          
          {job.status === 'failed' && (
            <div className="flex items-center text-sm text-red-600">
              <AlertCircle className="h-4 w-4 mr-2" />
              Failed
            </div>
          )}
        </div>
      </div>


      {/* Job Status and Progress */}
      <div className="mt-4 bg-white rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              job.status === 'completed' ? 'bg-green-100 text-green-800' :
              job.status === 'processing' ? 'bg-blue-100 text-blue-800' :
              job.status === 'failed' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
            </span>
            
            <div className="text-sm text-gray-600">
              {job.progress.processedPhotos} processed, {job.progress.skippedPhotos} skipped, {job.progress.failedPhotos} failed 
              of {job.progress.totalPhotos} total photos
              {job.config && (
                <span className="ml-3 text-gray-500">
                  • {job.config.style} style
                  {job.config.descriptionSentences && (
                    <span>, {job.config.descriptionSentences} sentences</span>
                  )}
                </span>
              )}
            </div>
          </div>
          
          {job.status === 'completed' && (
            <div className="text-sm text-gray-500">
              Completed: {new Date(job.completedAt).toLocaleString()}
            </div>
          )}
        </div>

        {/* Apply Progress Section */}
        {(() => {
          // Smart completion detection - hide progress bar if all photos are actually applied
          const processedPhotos = job.results?.filter(r => r.status === 'processed') || [];
          const appliedPhotos = processedPhotos.filter(r => r.applied);
          const allPhotosApplied = processedPhotos.length > 0 && appliedPhotos.length === processedPhotos.length;
          
          // Don't show progress bar if all photos are applied
          if (allPhotosApplied) {
            return false;
          }
          
          // Show progress bar if server or client indicates applying
          return job.applyProgress?.isApplying || batchApplying;
        })() && (
          <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <Upload className="h-5 w-5 mr-2 text-orange-600" />
                <h4 className="text-sm font-medium text-orange-800">Applying Metadata to {providerName}</h4>
              </div>
              <div className="text-sm text-orange-700">
                {(job.applyProgress?.appliedPhotos || 0) + (job.applyProgress?.failedApplies || 0)}/{job.applyProgress?.totalPhotosToApply || '...'} photos processed
              </div>
            </div>
            
            <div className="mb-2">
              <div className="flex justify-between text-sm text-orange-700 mb-1">
                <span>
                  {job.applyProgress?.appliedPhotos || 0} applied successfully
                  {(job.applyProgress?.failedApplies || 0) > 0 && (
                    <span className="text-red-600 ml-2">• {job.applyProgress.failedApplies} failed</span>
                  )}
                </span>
                <span>Batch {job.applyProgress?.currentApplyBatch || 1} of {job.applyProgress?.totalApplyBatches || '...'}</span>
              </div>
              <div className="w-full bg-orange-200 rounded-full h-3">
                <div 
                  className="bg-orange-600 h-3 rounded-full transition-all duration-500" 
                  style={{ width: `${job.applyProgressPercentage || 0}%` }}
                ></div>
              </div>
            </div>
            
            <div className="text-xs text-orange-600">
              <RefreshCw className="inline h-3 w-3 mr-1 animate-spin" />
              Applying metadata to your {providerName} photos in batches to respect rate limits...
            </div>
          </div>
        )}

      </div>

      {/* Payment Information Disclosure */}
      {job.payment && (job.payment.status === 'succeeded' || job.payment.status === 'free_trial') && (
        <div className="mt-4">
          <details className="group bg-white rounded-lg shadow-sm border border-gray-200">
            <summary className="cursor-pointer p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
              <div className="flex items-center space-x-3">
                {job.payment.status === 'free_trial' ? (
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                )}
                <div>
                  <h4 className="text-sm font-medium text-gray-900">
                    {job.payment.status === 'free_trial' ? 'Free Trial Usage' : 'Payment Receipt'}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {job.payment.status === 'free_trial' ? (
                      `${job.progress.totalPhotos} photos processed for free`
                    ) : (
                      `$${(job.payment.amount / 100).toFixed(2)} paid on ${new Date(job.payment.paidAt).toLocaleDateString()}`
                    )}
                  </p>
                </div>
              </div>
              
              <svg className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            
            <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
              {job.payment.status === 'free_trial' ? (
                /* Free Trial Details */
                <div className="py-4 text-sm">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Photos processed:</span>
                      <span className="font-medium text-blue-600">{job.progress.totalPhotos} photos</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cost:</span>
                      <span className="font-medium text-green-600">FREE (Trial)</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Processing style:</span>
                      <span className="font-medium text-gray-900">
                        {job.config?.style || 'descriptive'}
                        {job.config?.descriptionSentences && `, ${job.config.descriptionSentences} sentences`}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Processed on:</span>
                      <span className="font-medium text-gray-900">
                        {new Date(job.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 text-center">
                      🎉 This job was processed using your free trial
                    </p>
                  </div>
                </div>
              ) : (
                /* Paid Job Details */
                <div className="grid grid-cols-2 gap-4 py-4 text-sm">
                  <div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-medium text-gray-900">
                          ${(job.payment.amount / 100).toFixed(2)} {job.payment.currency?.toUpperCase() || 'USD'}
                        </span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Photos processed:</span>
                        <span className="font-medium text-gray-900">{job.progress.totalPhotos}</span>
                      </div>
                      
                      {job.payment.freePhotosUsed > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Free trial used:</span>
                          <span className="font-medium text-green-600">{job.payment.freePhotosUsed} photos</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Rate:</span>
                        <span className="font-medium text-gray-900">{PRICING.getDisplayRate()} per photo</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment date:</span>
                        <span className="font-medium text-gray-900">
                          {new Date(job.payment.paidAt).toLocaleString()}
                        </span>
                      </div>
                      
                      {job.payment.stripePaymentIntentId && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Payment ID:</span>
                          <span className="font-mono text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                            {job.payment.stripePaymentIntentId.substring(0, 20)}...
                          </span>
                        </div>
                      )}
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Paid
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Support Contact Info */}
              <div className="pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  {job.payment.status === 'free_trial' ? (
                    "Questions about your free trial? Contact support for assistance"
                  ) : (
                    `Need help with billing? Contact support with Payment ID: ${job.payment.stripePaymentIntentId}`
                  )}
                </p>
              </div>
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default JobHeader;
